/**
 * A category's own menu: what this category is, the things people restock most,
 * and a way in for everything else.
 *
 * It is a menu, not a form — nothing here is typed. Choosing a common item hands
 * a fully filled draft to the add sheet; "Custom item" hands it an empty one.
 */


import { CATEGORY_RULES } from "../features/food/categories";
import { COMMON_ITEMS } from "../features/food/commonItems";
import type { CommonItem } from "../features/food/commonItems";
import { LOCATION_LABELS } from "../features/food/types";
import type { FoodCategory } from "../features/food/types";
import { useFocusTrap } from "../lib/a11y/useFocusTrap";
import { CATEGORY_ICONS, Icon } from "./Icon";

import "./CategoryMenu.css";

interface CategoryMenuProps {
  category: FoodCategory;
  onPick: (item: CommonItem) => void;
  onCustom: () => void;
  onClose: () => void;
}

export function CategoryMenu({ category, onPick, onCustom, onClose }: CategoryMenuProps) {
  const rule = CATEGORY_RULES[category];
  const items = COMMON_ITEMS[category];

  // Its own dialog rather than a Sheet, so it needs the trap wiring directly.
  // Escape comes with it: one behaviour, one place, no dialog able to
  // implement two thirds of it and look finished.
  const panelRef = useFocusTrap<HTMLDivElement>({ onEscape: onClose });

  return (
    <div
      className="menu__scrim"
      // Only a press that starts on the scrim itself closes, so a drag that
      // began inside the panel doesn't dismiss it.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="menu"
        role="dialog"
        aria-modal="true"
        aria-labelledby="menu-title"
        ref={panelRef}
      >
        <button className="menu__close" type="button" onClick={onClose} aria-label="Close">
          <Icon name="close" />
        </button>

        <header className="menu__header">
          <Icon name={CATEGORY_ICONS[category]} className="menu__icon" />
          <h2 className="menu__title" id="menu-title">
            {rule.label}
          </h2>
          {/* The category's defaults, stated rather than hidden in the code. */}
          <p className="menu__defaults">
            {LOCATION_LABELS[rule.defaultLocation]} ·{" "}
            {rule.shelfLifeDays[rule.defaultLocation]} days · {rule.defaultUnit}
          </p>
        </header>

        {items.length > 0 && (
          <ul className="menu__items">
            {items.map((item) => (
              <li key={item.name}>
                <button className="menu__item" type="button" onClick={() => onPick(item)}>
                  <Icon name={CATEGORY_ICONS[category]} className="menu__item-icon" />
                  <span className="menu__item-text">
                    <span className="menu__item-name">{item.name}</span>
                    <span className="menu__item-amount">
                      {item.quantity} {item.unit}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <button className="menu__custom" type="button" onClick={onCustom}>
          Custom item
        </button>
      </div>
    </div>
  );
}
